// ─────────────────────────────────────────────────────────────────────────────
// HomeSection — Project landing screen.
// Construction Engineering Cockpit aesthetic. Token-driven.
//
// PHASE 4 (B + A): refactored to consume shared primitives from `./ui.jsx`.
// The page-specific bits that remain inline are:
//   - the icon set (page-specific)
//   - the hero card composition (one-of-a-kind)
//   - the BOQ/commercial calculation logic (preserved exactly from original)
//
// Everything else — Stat, Card, Button, Input, Reveal, ProgressRing, etc. —
// is shared and reused across the rest of the app.
// ─────────────────────────────────────────────────────────────────────────────

import React, { memo, useMemo } from "react";
import { currency } from "../utils/formatters";
import { addContractDaysAfter } from "../utils/contractCalendar";
import { tokens } from "./theme";
import GccDueCompletionPhasedSection, {
  buildGccPhasedScheduleSummary,
} from "./GccDueCompletionPhasedSection";
import {
  // Layout
  Card, BlueprintGrid, Reveal, Eyebrow,
  // Forms
  Input, Select, FormRow, FormGroupDivider,
  // Buttons
  Button,
  // Data
  Stat, DataField, ProgressRing,
  // Navigation tile
  QuickTile,
} from "./ui";

const t = tokens;

// ─── Helpers (preserved from original) ───────────────────────────────────────
function numberOrZero(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]/g, "");
    if (!cleaned) return 0;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

const DEFAULT_CONTINGENCY_RATE = 10;
const DEFAULT_VAT_RATE = 15;

function normaliseCpaType(value) {
  return String(value || "GCC").toUpperCase() === "FIDIC" ? "FIDIC" : "GCC";
}

// ─── Time-based programme progress ───────────────────────────────────────────
// progress = (current date − start date) / (revised end − start date)
// Falls back to the estimated/completion date when no revised date is set.
// A Closed project keeps its stored progress value (no longer time-driven).
function timeProgress(src) {
  if (!src) return 0;
  if (src.status === "Closed") return numberOrZero(src.progress);

  const start = src.startDate;
  const end = src.revisedEndDate || src.estimatedEndDate || src.completionDate;
  if (!start || !end) return 0;

  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;

  const total = e.getTime() - s.getTime();
  if (total <= 0) return 0;

  const elapsed = Date.now() - s.getTime();
  const pct = (elapsed / total) * 100;
  return Math.max(0, Math.min(100, pct));
}

// ─── Page-specific icons (Lucide-style SVGs) ─────────────────────────────────
const I = {
  add:        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  edit:       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>,
  trash:      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>,
  save:       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  cancel:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  cashflow:   <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  cert:       <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/></svg>,
  variation:  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>,
  rfi:        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  si:         <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  diary:      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  boq:        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  dashboard:  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg>,
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
function HomeSection({
  currentProject,
  projectForm,
  setProjectForm,
  isEditingProject,
  isAddingProject,
  beginEditProject,
  saveProject,
  cancelProjectEdit,
  goToSection,
  boqSections = [],
  clients = [],
  claims = [],
}) {
  // ── Commercial calculations (logic preserved exactly from original) ────────
  const contingencyRate = numberOrZero(
    projectForm.contingencyRate ?? currentProject?.contingencyRate ?? DEFAULT_CONTINGENCY_RATE
  );
  const vatRate = numberOrZero(
    projectForm.vatRate ?? currentProject?.vatRate ?? DEFAULT_VAT_RATE
  );

  const relevantBoqSections = Array.isArray(boqSections)
    ? boqSections.filter((item) => {
        if (!item) return false;
        if (!currentProject?.id) return true;
        if (!item.projectId) return true;
        return item.projectId === currentProject.id;
      })
    : [];

  const boqScheduledWorks = relevantBoqSections.reduce((sum, item) => (
    sum + numberOrZero(
      item.tenderedAmount ?? item.contractValue ?? item.sectionTotal ?? item.total ?? 0
    )
  ), 0);

  const liveScheduledWorks = boqScheduledWorks > 0
    ? boqScheduledWorks
    : numberOrZero(currentProject?.scheduledWorks);
  const liveContingencies = liveScheduledWorks * (contingencyRate / 100);
  const liveVat = (liveScheduledWorks + liveContingencies) * (vatRate / 100);
  const liveGrandTotal = liveScheduledWorks + liveContingencies + liveVat;

  const isEditing = isEditingProject || isAddingProject;
  const activeProjectId = currentProject?.id || projectForm?.id;
  const awardedClaimDays = (claims || [])
    .filter((item) => !activeProjectId || item.projectId === activeProjectId)
    .filter((item) => String(item.status || "") === "Awarded")
    .reduce((sum, item) => sum + numberOrZero(item.daysAwarded || item.daysClaimed), 0);
  const handleDueCompletionPhasedChange = (payload = {}) => {
    if (!payload.dueDate) return;
    const revisedEndDate =
      awardedClaimDays > 0
        ? addContractDaysAfter(payload.dueDate, awardedClaimDays, projectForm)
        : payload.dueDate;
    setProjectForm((prev) => {
      const next = {
        ...prev,
        startDate: payload.commencementDate || prev.startDate,
        contractDurationLabel: payload.contractDurationLabel || prev.contractDurationLabel,
        contractDurationValue: payload.contractDurationValue ?? prev.contractDurationValue,
        contractDurationUnit: payload.contractDurationUnit || prev.contractDurationUnit,
        estimatedEndDate: payload.dueDate,
        completionDate: payload.dueDate,
        revisedEndDate,
        dueCompletionPhased: payload.dueCompletionPhased,
      };
      if (
        prev.startDate === next.startDate &&
        prev.contractDurationLabel === next.contractDurationLabel &&
        prev.contractDurationValue === next.contractDurationValue &&
        prev.contractDurationUnit === next.contractDurationUnit &&
        prev.estimatedEndDate === next.estimatedEndDate &&
        prev.completionDate === next.completionDate &&
        prev.revisedEndDate === next.revisedEndDate &&
        prev.dueCompletionPhased === next.dueCompletionPhased
      ) {
        return prev;
      }
      return next;
    });
  };

  // Live time-based progress: (today − start) / (revised end − start).
  const programmeProgress = timeProgress(currentProject);
  const programmeProgressDisplay = Math.round(programmeProgress);
  const phasedScheduleSummary = useMemo(
    () => buildGccPhasedScheduleSummary(currentProject),
    [currentProject]
  );
  const hasPhaseDueDates = !!phasedScheduleSummary?.phases?.length;
  const hasMultipleEndDates = phasedScheduleSummary?.hasMultiplePhases === true;
  const fallbackDurationLabel =
    currentProject?.contractDurationLabel ||
    (currentProject?.contractDurationDays !== undefined &&
    currentProject?.contractDurationDays !== null &&
    String(currentProject.contractDurationDays).trim() !== ""
      ? `${currentProject.contractDurationDays} calendar days`
      : "");
  const overviewStartDate = phasedScheduleSummary?.earliestStart || currentProject?.startDate;
  const overviewDurationLabel = hasMultipleEndDates
    ? `${phasedScheduleSummary.phaseCount} phases · ${phasedScheduleSummary.combinedDurationLabel}`
    : phasedScheduleSummary?.activePhase?.durationLabel || fallbackDurationLabel;
  const overviewEstimatedEndDate = hasMultipleEndDates
    ? phasedScheduleSummary?.finalDueDate
    : phasedScheduleSummary?.activeDueDate || currentProject?.estimatedEndDate || currentProject?.completionDate;
  const overviewRevisedEndDate =
    hasMultipleEndDates && overviewEstimatedEndDate
      ? (
          awardedClaimDays > 0
            ? addContractDaysAfter(overviewEstimatedEndDate, awardedClaimDays, currentProject || {})
            : overviewEstimatedEndDate
        )
      : currentProject?.revisedEndDate || overviewEstimatedEndDate;

  // ── Sparkline points — cosmetic S-curve seeded by current progress ────────
  const sparkPoints = useMemo(() => {
    const p = Math.max(0.05, Math.min(0.95, programmeProgress / 100 || 0.18));
    return Array.from({ length: 12 }, (_, i) => {
      const x = i / 11;
      const s = 1 / (1 + Math.exp(-12 * (x - 0.5)));
      return Math.min(s, p + (1 - p) * 0.02 * i);
    });
  }, [programmeProgress]);

  const statusColor =
    currentProject?.status === "Active" ? t.color.success
    : currentProject?.status === "Closed" ? t.color.inkMuted
    : t.color.warning;

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <section style={{
      display: "grid", gap: 18,
      fontFamily: t.font.body,
      position: "relative",
    }}>
      <BlueprintGrid />

      <div style={{ position: "relative", zIndex: 1, display: "grid", gap: 18 }}>

        {/* ───── HERO ───── */}
        <Reveal delay={0}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.25fr) minmax(320px, 0.75fr)",
            gap: 18, alignItems: "stretch",
          }}>
            <Card padded={false} accent style={{ borderRadius: 14 }}>
              <div style={{
                padding: 26,
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                gap: 24,
                alignItems: "start",
              }}>
                <div style={{ minWidth: 0 }}>
                  {currentProject?.id ? (
                    <div style={{
                      display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
                    }}>
                      <Eyebrow>Project Register</Eyebrow>
                      <span style={{ width: 3, height: 3, borderRadius: "50%", background: t.color.borderStrong }} />
                      <span style={{
                        fontFamily: t.font.mono,
                        fontSize: 11, fontWeight: 600,
                        color: t.color.inkMuted,
                        letterSpacing: "0.02em",
                      }}>{currentProject.id}</span>
                      {currentProject?.status && (
                        <>
                          <span style={{ width: 3, height: 3, borderRadius: "50%", background: t.color.borderStrong }} />
                          <span style={{
                            fontFamily: t.font.body,
                            fontSize: 10, fontWeight: 700,
                            letterSpacing: "0.12em", textTransform: "uppercase",
                            color: statusColor,
                          }}>● {currentProject.status}</span>
                        </>
                      )}
                    </div>
                  ) : (
                    <Eyebrow>Project Register</Eyebrow>
                  )}

                  <h2 style={{
                    margin: currentProject?.id ? "0" : "10px 0 0",
                    fontFamily: t.font.display,
                    fontSize: 32, fontWeight: 700,
                    lineHeight: 1.05, letterSpacing: "-0.035em",
                    color: t.color.ink,
                  }}>
                    {currentProject?.name || "Project Register"}
                  </h2>

                  <p style={{
                    margin: "14px 0 0",
                    fontSize: 13, lineHeight: 1.6,
                    color: t.color.inkMuted, maxWidth: 640,
                  }}>
                    Core contract data drives the commercial baseline, programme controls
                    and projected completion dates across every section. Keep this
                    register clean and the whole cockpit stays in sync.
                  </p>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 22 }}>
                    {!isEditing && currentProject?.id && (
                      <Button onClick={beginEditProject} variant="outline" icon={I.edit}>
                        Edit Project
                      </Button>
                    )}
                    {isEditing && (
                      <>
                        <Button onClick={saveProject} variant="primary" icon={I.save}>
                          Save Project
                        </Button>
                        <Button onClick={cancelProjectEdit} variant="outline" icon={I.cancel}>
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {!isEditing && (
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "4px 0",
                  }}>
                    <ProgressRing progress={programmeProgressDisplay} size={96} stroke={9} />
                  </div>
                )}
              </div>
            </Card>

            {/* Commercial dashboard — 2×2 stat grid */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 10,
              alignContent: "stretch",
            }}>
              <Stat
                label="Scheduled Works"
                rawValue={liveScheduledWorks}
                format={(v) => currency(v)}
                value={currency(liveScheduledWorks)}
                helper="Base contract value, excl. contingency & VAT"
                accent={t.color.primary}
                delay={120}
              />
              <Stat
                label="Contingencies"
                rawValue={liveContingencies}
                format={(v) => currency(v)}
                value={currency(liveContingencies)}
                helper={`${contingencyRate}% of scheduled works`}
                accent={t.color.warning}
                delay={200}
              />
              <Stat
                label="VAT"
                rawValue={liveVat}
                format={(v) => currency(v)}
                value={currency(liveVat)}
                helper={`Calculated at ${vatRate}%`}
                accent={t.color.success}
                delay={280}
              />
              <Stat
                label="Grand Total"
                rawValue={liveGrandTotal}
                format={(v) => currency(v)}
                value={currency(liveGrandTotal)}
                helper="Works + contingency + VAT"
                accent={t.color.accent}
                sparkPoints={sparkPoints}
                delay={360}
              />
            </div>
          </div>
        </Reveal>

        {/* ───── PROJECT OVERVIEW (read mode) ───── */}
        {!isEditing && (
          <Reveal delay={240}>
            <Card padded={false} style={{ borderRadius: 14 }}>
              <div style={{ padding: "20px 22px 4px" }}>
                <Eyebrow>Contract · Identity</Eyebrow>
                <div style={{
                  display: "flex", alignItems: "baseline", justifyContent: "space-between",
                  gap: 16, flexWrap: "wrap", marginTop: 6,
                }}>
                  <h3 style={{
                    margin: 0,
                    fontFamily: t.font.display,
                    fontSize: 20, fontWeight: 700,
                    letterSpacing: "-0.02em", color: t.color.ink,
                  }}>Project Overview</h3>
                  <div style={{
                    fontFamily: t.font.body, fontSize: 11, fontWeight: 500,
                    color: t.color.inkSubtle,
                  }}>
                    <span style={{ fontFamily: t.font.mono, color: t.color.ink, fontWeight: 600 }}>
                      {relevantBoqSections.length}
                    </span>{" "}
                    BOQ sections loaded · scheduled works total{" "}
                    <span style={{ fontFamily: t.font.mono, color: t.color.ink, fontWeight: 600 }}>
                      {currency(boqScheduledWorks)}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ padding: 22, paddingTop: 16 }}>
                <div style={{ display: "grid", gap: 14 }}>
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 10,
                  }}>
                    <DataField label="Client" value={currentProject?.client} />
                    <DataField label="Department" value={currentProject?.department} />
                    <DataField label="Contract Number" value={currentProject?.contractNumber || currentProject?.contractNo} mono />
                    <DataField label="Contract Description" value={currentProject?.contractDescription || currentProject?.description || currentProject?.name} />
                    <DataField label="Contractor" value={currentProject?.contractor} />
                    <DataField label="Contract Type" value={currentProject?.contractType || "GCC"} mono />
                    <DataField label="CPA Type" value={normaliseCpaType(currentProject?.cpaType || currentProject?.contractType)} mono />
                    <DataField label="Status" value={currentProject?.status} />
                  </div>

                  <div style={{
                    border: `1px solid ${t.color.border}`,
                    borderRadius: 12,
                    background: "linear-gradient(180deg, rgba(248,250,252,0.96), rgba(255,255,255,0.96))",
                    padding: 14,
                  }}>
                    <div style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 12,
                      flexWrap: "wrap",
                      marginBottom: 10,
                    }}>
                      <div>
                        <div style={{
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.16em",
                          textTransform: "uppercase",
                          color: t.color.primary,
                        }}>
                          Due Completion
                        </div>
                        <div style={{
                          marginTop: 4,
                          fontSize: 12,
                          color: t.color.inkMuted,
                        }}>
                          {hasMultipleEndDates
                            ? "Multiple contractual completion dates from the phase calculator"
                            : "Single completion date from the project programme rules"}
                        </div>
                      </div>
                      {hasMultipleEndDates && (
                        <span style={{
                          border: `1px solid ${t.color.border}`,
                          borderRadius: 999,
                          padding: "5px 9px",
                          fontFamily: t.font.mono,
                          fontSize: 11,
                          fontWeight: 700,
                          color: t.color.ink,
                          background: "#fff",
                        }}>
                          {phasedScheduleSummary.phaseCount} dates
                        </span>
                      )}
                    </div>

                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                      gap: 10,
                    }}>
                      <DataField label={hasMultipleEndDates ? "Overall Start" : "Start Date"} value={overviewStartDate} mono />
                      <DataField label={hasMultipleEndDates ? "Total Duration" : "Contract Duration"} value={overviewDurationLabel} mono />
                      <DataField label={hasMultipleEndDates ? "Final Due Date" : "Estimated End"} value={overviewEstimatedEndDate} mono />
                      <DataField label="Revised End" value={overviewRevisedEndDate} mono />
                      <DataField label="Progress" value={`${programmeProgressDisplay}%`} mono />
                    </div>

                    {hasPhaseDueDates && (
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 10,
                        marginTop: 10,
                      }}>
                        {phasedScheduleSummary.phases.map((phase) => (
                          <div key={phase.id} style={{
                            border: `1px solid ${t.color.border}`,
                            borderRadius: 10,
                            background: "#fff",
                            padding: 12,
                            minWidth: 0,
                          }}>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 8,
                              marginBottom: 7,
                            }}>
                              <strong style={{
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: 13,
                                color: t.color.ink,
                              }}>
                                {phase.name}
                              </strong>
                              {phase.linkedToPrevious && (
                                <span style={{
                                  flex: "0 0 auto",
                                  fontSize: 9,
                                  fontWeight: 800,
                                  letterSpacing: "0.1em",
                                  textTransform: "uppercase",
                                  color: t.color.primary,
                                }}>
                                  Linked
                                </span>
                              )}
                            </div>
                            <div style={{
                              fontFamily: t.font.mono,
                              fontSize: 18,
                              fontWeight: 800,
                              color: t.color.ink,
                            }}>
                              {phase.dueDate || "-"}
                            </div>
                            <div style={{
                              marginTop: 7,
                              display: "grid",
                              gap: 3,
                              fontSize: 11,
                              color: t.color.inkMuted,
                              lineHeight: 1.35,
                            }}>
                              <span>Start {phase.displayCommencement || "-"}</span>
                              <span>{phase.durationLabel || "-"} · {phase.span || 0} calendar days span</span>
                              <span>{phase.required || 0} counted · {phase.excluded || 0} excluded</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 10,
                  }}>
                    <DataField label="Contingency Rate" value={`${contingencyRate}%`} mono />
                    <DataField label="VAT Rate" value={`${vatRate}%`} mono />
                    <DataField label="Scheduled Works" value={currency(liveScheduledWorks)} mono />
                    <DataField label="Contingencies" value={currency(liveContingencies)} mono />
                    <DataField label="VAT" value={currency(liveVat)} mono />
                    <DataField label="Grand Total" value={currency(liveGrandTotal)} mono />
                  </div>
                </div>
              </div>
            </Card>
          </Reveal>
        )}

        {/* ───── EDIT FORM ───── */}
        {isEditing && (
          <Reveal delay={240}>
            <Card padded={false} style={{ borderRadius: 14 }}>
              <div style={{ padding: "20px 22px 16px" }}>
                <Eyebrow>{isAddingProject ? "New record" : "Editing record"}</Eyebrow>
                <h3 style={{
                  margin: "6px 0 0",
                  fontFamily: t.font.display,
                  fontSize: 22, fontWeight: 700,
                  letterSpacing: "-0.025em", color: t.color.ink,
                }}>
                  {isAddingProject ? "Create Project" : "Edit Project"}
                </h3>
                <p style={{
                  margin: "6px 0 0",
                  fontSize: 13, color: t.color.inkMuted, lineHeight: 1.5,
                }}>
                  Capture project identity, contract form, commercial baseline and key dates.
                </p>
              </div>

              <div style={{ padding: "0 22px 22px" }}>
                <FormGroupDivider>Identity</FormGroupDivider>

                <FormRow label="Project Number" helper="Unique reference used throughout the system.">
                  <Input value={projectForm.id ?? ""}
                    onChange={(e) => setProjectForm({ ...projectForm, id: e.target.value })} />
                </FormRow>

                <FormRow label="Project Name" helper="Full project title for reports and dashboards.">
                  <Input value={projectForm.name ?? ""}
                    onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })} />
                </FormRow>

                <FormRow label="Client (linked)" helper="Links this project to a client record. Manage clients in Client Management.">
                  <Select
                    value={projectForm.clientId || ""}
                    onChange={(e) => {
                      const clientId = e.target.value;
                      const picked = clients.find((c) => c.id === clientId);
                      setProjectForm({
                        ...projectForm,
                        clientId,
                        // keep the free-text W1 label in sync with the chosen client
                        client: picked ? picked.name : projectForm.client,
                      });
                    }}
                  >
                    <option value="">— Unassigned —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                </FormRow>

                <FormRow label="Client (W1 label)" helper="Line 1 on the W1 payment summary (e.g. Northbridge Municipality). Auto-filled from the linked client, editable.">
                  <Input value={projectForm.client || ""}
                    onChange={(e) => setProjectForm({ ...projectForm, client: e.target.value })} />
                </FormRow>

                <FormRow label="Department" helper="Line 2 on the W1 payment summary.">
                  <Input value={projectForm.department || ""}
                    onChange={(e) => setProjectForm({ ...projectForm, department: e.target.value })} />
                </FormRow>

                <FormRow label="Contract Number" helper="Line 3 on the W1 payment summary — the formal contract reference.">
                  <Input value={projectForm.contractNumber || ""}
                    onChange={(e) => setProjectForm({ ...projectForm, contractNumber: e.target.value })} />
                </FormRow>

                <FormRow label="Contract Description" helper="Line 4 on the W1 payment summary, as it appears on the certificate.">
                  <Input value={projectForm.contractDescription || ""}
                    onChange={(e) => setProjectForm({ ...projectForm, contractDescription: e.target.value })} />
                </FormRow>

                <FormRow label="Contractor" helper="Line 5 on the W1 payment summary.">
                  <Input value={projectForm.contractor || ""}
                    onChange={(e) => setProjectForm({ ...projectForm, contractor: e.target.value })} />
                </FormRow>

	                <FormRow label="Contract Type" helper="Governing contract form.">
	                  <Select value={projectForm.contractType || "GCC"}
	                    onChange={(e) => setProjectForm({ ...projectForm, contractType: e.target.value })}>
                    <option value="GCC">GCC</option>
                    <option value="FIDIC">FIDIC</option>
                    <option value="JBCC">JBCC</option>
                    <option value="NEC">NEC</option>
	                  </Select>
	                </FormRow>

	                <FormRow label="CPA Type" helper="Controls the CPA register setup used by certificates and cashflow.">
	                  <Select
	                    value={normaliseCpaType(projectForm.cpaType || projectForm.contractType)}
	                    onChange={(e) => setProjectForm({ ...projectForm, cpaType: e.target.value })}
	                  >
	                    <option value="GCC">GCC</option>
	                    <option value="FIDIC">FIDIC</option>
	                  </Select>
	                </FormRow>

                <FormRow label="Status" helper="Planning, active or closed project state.">
                  <Select value={projectForm.status || "Active"}
                    onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}>
                    <option value="Planning">Planning</option>
                    <option value="Active">Active</option>
                    <option value="Closed">Closed</option>
                  </Select>
                </FormRow>

                <FormGroupDivider>Commercial Rates</FormGroupDivider>

                <FormRow
                  label="Contingency Rate (%)"
                  helper={`Percentage applied to scheduled works. Default ${DEFAULT_CONTINGENCY_RATE}%.`}
                >
                  <Input type="number" min="0" max="100" step="0.5"
                    value={projectForm.contingencyRate ?? DEFAULT_CONTINGENCY_RATE}
                    onChange={(e) => setProjectForm({
                      ...projectForm,
                      contingencyRate: numberOrZero(e.target.value),
                    })} />
                </FormRow>

                <FormRow
                  label="VAT Rate (%)"
                  helper={`Percentage applied to scheduled works plus contingency. Default ${DEFAULT_VAT_RATE}%.`}
                >
                  <Input type="number" min="0" max="100" step="0.5"
                    value={projectForm.vatRate ?? DEFAULT_VAT_RATE}
                    onChange={(e) => setProjectForm({
                      ...projectForm,
                      vatRate: numberOrZero(e.target.value),
                    })} />
                </FormRow>

                <FormGroupDivider>Commercial Summary</FormGroupDivider>

                <FormRow
                  label="Scheduled Works Value"
                  helper="Controlled from the BOQ page tendered totals. Locked here — update the BOQ register to change it."
                >
                  <Input type="number" value={liveScheduledWorks || ""} readOnly />
                </FormRow>

                <FormRow label="Contingencies" helper={`Calculated at ${contingencyRate}% of scheduled works.`}>
                  <Input value={currency(liveContingencies)} readOnly />
                </FormRow>

                <FormRow label="VAT" helper={`Calculated at ${vatRate}%.`}>
                  <Input value={currency(liveVat)} readOnly />
                </FormRow>

                <FormRow label="Grand Total" helper="Scheduled works plus contingency and VAT.">
                  <Input value={currency(liveGrandTotal)} readOnly />
                </FormRow>

	                <FormGroupDivider>Dates &amp; Programme</FormGroupDivider>

	                <GccDueCompletionPhasedSection
	                  projectForm={projectForm}
	                  onScheduleChange={handleDueCompletionPhasedChange}
	                />

                <FormRow
                  label="Programme Progress"
                  helper="(today − start date) ÷ (revised end − start date). Falls back to the estimated end date when no revised date is set. If status is Closed, the stored value stays fixed."
                >
                  <Input value={`${Math.round(timeProgress(projectForm))}%`} readOnly />
                </FormRow>
              </div>
            </Card>
          </Reveal>
        )}

        {/* ───── QUICK ACTIONS ───── */}
        <Reveal delay={isEditing ? 320 : 380}>
          <Card padded={false} style={{ borderRadius: 14 }}>
            <div style={{ padding: "20px 22px 16px" }}>
              <Eyebrow>Navigate</Eyebrow>
              <h3 style={{
                margin: "6px 0 4px",
                fontFamily: t.font.display,
                fontSize: 20, fontWeight: 700,
                letterSpacing: "-0.02em", color: t.color.ink,
              }}>Quick Actions</h3>
              <p style={{
                margin: 0,
                fontSize: 12, color: t.color.inkMuted, lineHeight: 1.5,
              }}>
                Jump into the areas you use most for project controls and contract administration.
              </p>
            </div>

            <div style={{
              padding: "0 22px 22px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 12,
            }}>
              <QuickTile icon={I.cashflow}  title="Budget & Cashflow"   text="Track expenditure curve against the project lifecycle."             buttonLabel="Open" onClick={() => goToSection("cashflow")} />
              <QuickTile icon={I.cert}      title="Certificates"        text="Manage valuations and the certified position of the contract."      buttonLabel="Open" onClick={() => goToSection("certificates")} />
              <QuickTile icon={I.variation} title="Variation Orders"    text="Track additional work, omissions and commercial impact."            buttonLabel="Open" onClick={() => goToSection("variations")} />
              <QuickTile icon={I.rfi}       title="RFIs"                text="Capture technical queries and any commercial change they trigger."  buttonLabel="Open" onClick={() => goToSection("rfis")} />
              <QuickTile icon={I.si}        title="Site Instructions"   text="Control contractor instructions and link them to change events."    buttonLabel="Open" onClick={() => goToSection("instructions")} />
              <QuickTile icon={I.diary}     title="Daily Diaries"       text="Maintain the site record of work, weather and sign-off."            buttonLabel="Open" onClick={() => goToSection("diary")} />
              <QuickTile icon={I.boq}       title="BOQ Register"        text="Manage sections, tender totals and the commercial baseline."        buttonLabel="Open" onClick={() => goToSection("boq")} />
              <QuickTile icon={I.dashboard} title="Financial Dashboard" text="Revised totals, claims and variation exposure in one view."         buttonLabel="Open" onClick={() => goToSection("financial")} />
            </div>
          </Card>
        </Reveal>

      </div>
    </section>
  );
}

export default memo(HomeSection);
